import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';
import { ApolloCache, FetchResult } from '@apollo/client';
import {
  LabelType,
  LabelValueAcceptedBy,
  LabelValueSource,
  PutLabelSuggestionsMutation,
  SortOrder,
  SplitTimeSingalMutation,
  TimeSignal,
  TimeSignalAcceptedBy,
  TimeSignalAttribute,
  TimeSignalClassification,
  TimeSignalsDocument,
  TimeSignalsQuery,
  UpdateTimeSignalMutation,
  usePutLabelSuggestionsMutation,
  useSplitTimeSingalMutation
} from 'graphql/types';
import { mapDateToISOString } from 'utils/date-time';
import {
  ALL_CLASSIFICATIONS,
  TimeSignalGetParam,
  useTimeSignalGetParam
} from 'modules/notes/hooks/useTimeSignalParam';
import {
  NotesClassification,
  NOTES_PAGE_TYPE,
  useNotesFilterContext
} from 'modules/notes/NotesFilterContextProvider';
import { getTime } from 'modules/notes/addNote/hooks';
import { useMeContext } from 'modules/me';
import { TIMELINE_TIMESIGNAL_COUNT } from 'modules/notes/Timeline/hooks';
import {
  FormTimesheetLabel,
  TimesheetLabel
} from 'modules/timesheet-labels/labelSchema';
import { mapTimesheetLabel, SearchCriteria } from 'modules/notes/common';
import { useNotesToastContext } from 'modules/notes/NotesToastContextProvider';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import {
  isSearchCriteriaMatch,
  shouldAddToCache,
  sortedTimeSignals,
  updateCachedTimeSignalsWithFederatedSuggestions
} from 'modules/notes/cache/utils/cache-utils';
import { useNotesSortContext } from 'modules/notes/NotesSortContextProvider';
import { useNotesAnimationContext } from 'modules/notes/providers';
import { NotesActions } from 'modules/notes/notesSelector/hooks';
import { clearCachedDates } from 'modules/notes/cache/utils/time-signal-utils';
import { OFFSET_ENTRY } from '../types';
import { truncateMessage } from '../../common/utils';
import { SplitNoteFormData } from '../splitNoteSchema';
import { useFeatureFlag } from '../../../common/hooks';
import { shouldAcceptSuggestion } from '../../../common/utils/shouldAcceptSuggestion';

type ReturnType = { saveSplitNote: () => void; loading: boolean };

export const canAddNoteToCache = (
  param: TimeSignalGetParam,
  currentClassification: NotesClassification,
  timeSignal: TimeSignal,
  searchCriteria: SearchCriteria
): boolean => {
  if (
    currentClassification === 'All' &&
    !ALL_CLASSIFICATIONS.includes(timeSignal.classification)
  ) {
    return false;
  } else if (
    currentClassification !== 'All' &&
    TimeSignalClassification[currentClassification] !==
      timeSignal.classification
  ) {
    return false;
  }

  if (
    timeSignal.date < param.input.filter?.fromDate ||
    timeSignal.date > param.input.filter?.toDate
  ) {
    return false;
  }
  if (!isSearchCriteriaMatch(timeSignal, searchCriteria)) {
    return false;
  }
  const searchText =
    timeSignal.labelValues.reduce((acc, label) => {
      return label.value?.stringValue ? acc + label.value.stringValue : acc;
    }, '') + timeSignal.description;

  const keywords =
    param.input.filter?.searchText?.toLowerCase().split(/\s+/) ?? [];
  return keywords.length === 0
    ? true
    : keywords.every((key) => searchText.toLowerCase().includes(key));
};

export const appendTimeSignals = (
  filteredSignals: TimeSignal[],
  entriesToAdd: TimeSignal[],
  entriesCanAdd: TimeSignal[],
  sortOrder: SortOrder
): TimeSignal[] => {
  if (entriesToAdd.length > 0) {
    const date = DateTime.fromISO(entriesToAdd[0].date);
    const beforeItems = filteredSignals.filter(
      (s) => DateTime.fromISO(s.date) > date
    );
    const items = filteredSignals.filter((s) =>
      DateTime.fromISO(s.date).equals(date)
    );
    const afterItems = filteredSignals.filter(
      (s) => DateTime.fromISO(s.date) < date
    );

    const entries = entriesToAdd.reduce(
      (acc: TimeSignal[], entry: TimeSignal) => {
        acc = sortedTimeSignals(acc, sortOrder, entry);

        return acc;
      },
      items
    );

    return [...beforeItems, ...entries, ...afterItems];
  } else {
    return entriesCanAdd.reduce((entries, entry) => {
      const date = DateTime.fromISO(entry.date);
      const beforeItems = entries.filter(
        (s) => DateTime.fromISO(s.date) > date
      );
      const afterItems = entries.filter(
        (s) => DateTime.fromISO(s.date) <= date
      );
      return [...beforeItems, entry, ...afterItems];
    }, filteredSignals);
  }
};

export const updateCreateCache = ({
  param,
  currentClassification,
  originalNoteId,
  searchCriteria,
  sortOrder
}: {
  param: TimeSignalGetParam;
  currentClassification: NotesClassification;
  originalNoteId: string;
  searchCriteria: SearchCriteria;
  sortOrder: SortOrder;
}) => (
  cache: ApolloCache<SplitTimeSingalMutation>,
  mutationResult: FetchResult<
    SplitTimeSingalMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (mutationResult?.data?.splitTimeSignal) {
    const existingSignals: TimeSignalsQuery | null = cache.readQuery({
      query: TimeSignalsDocument,
      variables: param
    });
    if (existingSignals) {
      const entriesCanAdd = mutationResult.data.splitTimeSignal.timeSignals.filter(
        (signal) =>
          canAddNoteToCache(
            param,
            currentClassification,
            signal,
            searchCriteria
          )
      );
      const entriesToAdd = entriesCanAdd.filter((signal) =>
        shouldAddToCache(existingSignals, signal, sortOrder)
      );
      const filteredSignals = existingSignals.timeSignals.items.filter(
        (entry) => entry.id !== originalNoteId
      );
      const orderedSignals = appendTimeSignals(
        filteredSignals,
        entriesToAdd,
        entriesCanAdd,
        sortOrder
      );

      cache.writeQuery({
        query: TimeSignalsDocument,
        variables: param,
        data: {
          timeSignals: entriesToAdd.length
            ? {
                __typename: 'TimeSignalsResult',
                items: orderedSignals,
                cursor: existingSignals.timeSignals.cursor,
                totalCount:
                  existingSignals.timeSignals.totalCount +
                  entriesCanAdd.length -
                  1
              }
            : {
                __typename: 'TimeSignalsResult',
                items: filteredSignals,
                cursor: existingSignals.timeSignals.cursor,
                totalCount: existingSignals.timeSignals.totalCount - 1
              }
        }
      });
    }
  }
};

const updateCacheWithFederatedSuggestions = (param: TimeSignalGetParam) => (
  cache: ApolloCache<UpdateTimeSignalMutation>,
  mutationResult: FetchResult<PutLabelSuggestionsMutation>
): void => {
  if (mutationResult?.data?.putLabelSuggestions) {
    updateCachedTimeSignalsWithFederatedSuggestions(
      cache,
      mutationResult?.data?.putLabelSuggestions,
      param
    );
  }
};

const getMessageId = (
  selectedFolder: NotesClassification,
  targetClassification: TimeSignalClassification
) => {
  return selectedFolder === 'All' ||
    selectedFolder.toLocaleLowerCase() ===
      targetClassification.toLocaleLowerCase()
    ? 'toast.note.split.successfulSplitTo.common'
    : `toast.note.split.successfulSplitTo.${targetClassification.toLocaleLowerCase()}`;
};

const acceptSuggestion = (
  labels: TimesheetLabel[],
  isTimeSignalFederatedSuggestionsEnabled: boolean,
  isEmptyLabelsEnabled: boolean
): TimesheetLabel[] => {
  const labelTypesToIgnore = [LabelType.Comment];
  return labels.map((label) => {
    if (shouldAcceptSuggestion(label, isEmptyLabelsEnabled)) {
      if (labelTypesToIgnore.includes(label.type)) {
        return label;
      }

      label.value = label.suggestedValues?.[0];
    }
    if (
      isTimeSignalFederatedSuggestionsEnabled &&
      label.valueSource === LabelValueSource.Suggestion &&
      !label.acceptedBy
    ) {
      // Backwards compatibility: If federatedSuggestions is enabled there could be a legacy suggestion at label.value that is invisible to the user.
      // however the visible federated suggestion the user sees and meant to accept is at label.suggestedValues[0]
      // So if there is a non accepted label.value and federatedSuggestions is enabled then we overwrite it.
      label.value = label.suggestedValues?.[0];
    }
    label.acceptedBy = LabelValueAcceptedBy.User;
    return label;
  });
};

export const useSaveSplitNote = ({
  note,
  attributes,
  onComplete
}: {
  note: TimeSignal;
  onComplete: () => void;
  attributes?: TimeSignalAttribute[];
}): ReturnType => {
  const {
    filterContext: { selectedClassification, pageType, searchCriteria }
  } = useNotesFilterContext();
  const { id, locale } = useMeContext() || {};
  const ianaName = locale?.timeZone?.ianaName;
  const [splitTimeSignal, { loading }] = useSplitTimeSingalMutation();
  const [
    putSuggestions,
    { loading: putSuggestionsLoading }
  ] = usePutLabelSuggestionsMutation();
  const param = useTimeSignalGetParam(
    pageType === NOTES_PAGE_TYPE.TIMELINE
      ? TIMELINE_TIMESIGNAL_COUNT
      : undefined
  );
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const isEmptyLabelsEnabled = useFeatureFlag('isEmptyLabelsEnabled', false);

  const me = useMeContext();
  const { setToastContext } = useNotesToastContext();
  const { sortOrder } = useNotesSortContext();
  const { setNotesToAnimate } = useNotesAnimationContext();

  const { handleSubmit } = useFormContext();
  const splitNote = useCallback(
    async (data: SplitNoteFormData) => {
      const input = {
        id: note.id,
        timeSignals: data.splitEntries
          .filter((splitEntry) => splitEntry.id !== OFFSET_ENTRY)
          .map((splitEntry) => {
            let startTime = splitEntry.startTime;
            let endTime = splitEntry.endTime;
            const noteDate = DateTime.fromISO(note.date).toJSDate();
            const timesheetLabels: TimesheetLabel[] = (
              splitEntry.timesheetLabels || []
            ).map(({ label }: FormTimesheetLabel) => label);
            let labelValues = [
              ...timesheetLabels,
              ...(splitEntry.customLabels || [])
            ];

            if (
              isTimeSignalFederatedSuggestionsEnabled &&
              data.classification === TimeSignalClassification.Accepted
            ) {
              labelValues = acceptSuggestion(
                labelValues,
                isTimeSignalFederatedSuggestionsEnabled,
                isEmptyLabelsEnabled
              );
            }

            if (splitEntry.date && startTime) {
              startTime = getTime(noteDate, startTime, ianaName);
            }
            if (splitEntry.date && endTime) {
              endTime = getTime(noteDate, endTime, ianaName);
            }
            return {
              date: note.date,
              sources: note.sources.map((source) => ({
                id: source.id,
                name: source.name,
                ref: source.name,
                image: source.image ? { src: source.image?.src } : null
              })),
              classification: data.classification,
              description: splitEntry.description,
              startTime: mapDateToISOString(startTime),
              endTime: mapDateToISOString(endTime),
              duration: Math.round(splitEntry.duration ?? 0),
              attributes: (attributes ?? []).map((attribute) => ({
                name: attribute.name,
                value: attribute.value,
                sourceId: attribute.sourceId,
                sourceRefs: attribute.sourceRefs,
                visible: attribute.visible
              })),
              labelValues: labelValues,
              labelValuesInferenceId: splitEntry.labelValuesInferenceId
            };
          })
      };

      try {
        clearCachedDates(id);

        const result = await splitTimeSignal({
          variables: {
            input: {
              id: input.id,
              timeSignals: input.timeSignals.map((ts) => ({
                ...ts,
                labelValues: ts.labelValues.map((l) =>
                  mapTimesheetLabel(isSmartSuggestionEnabled, l)
                ),
                acceptedBy:
                  ts.classification === TimeSignalClassification.Accepted
                    ? TimeSignalAcceptedBy.User
                    : undefined
              }))
            }
          },
          update: updateCreateCache({
            param,
            currentClassification: selectedClassification,
            originalNoteId: note.id,
            searchCriteria,
            sortOrder
          }),
          awaitRefetchQueries: true,
          refetchQueries: ['timeSignalsCount', 'timeSignalsTotals']
        });

        if (
          isTimeSignalFederatedSuggestionsEnabled &&
          result.data?.splitTimeSignal.timeSignals &&
          me?.id
        ) {
          result.data.splitTimeSignal.timeSignals.map((signal: TimeSignal) => {
            const newValues = input.timeSignals.find(
              (ts) =>
                ts.labelValuesInferenceId === signal.labelValuesInferenceId
            );

            if (signal.id && signal.labelValuesInferenceId && newValues) {
              putSuggestions({
                variables: {
                  input: {
                    userUri: me.id,
                    inferenceId: signal.labelValuesInferenceId,
                    timeSignalId: signal.id,
                    startTime: newValues.startTime,
                    endTime: newValues.endTime,
                    duration: newValues.duration ? newValues.duration : null,
                    suggestions: newValues
                      .labelValues!.filter(
                        (label) =>
                          Boolean(label) && label.type !== LabelType.Comment
                      )
                      .map(
                        (l: TimesheetLabel) =>
                          ({
                            labelId: l.id,
                            suggestionMetadata: l.suggestionMetadata,
                            thresholdMet: l.thresholdMet ?? false,
                            suggestedValues: l.suggestedValues ?? []
                          } as any)
                      )
                  }
                },
                update: updateCacheWithFederatedSuggestions(param)
              });
            }
          });
        }

        if (result.data?.splitTimeSignal.timeSignals.length) {
          setNotesToAnimate(
            result.data.splitTimeSignal.timeSignals.map((signal) => ({
              id: signal.id,
              action: 'SplitNote',
              folder: data.classification.toString() as NotesActions
            }))
          );
          setTimeout(() => setNotesToAnimate([]), 2000);
        }
        setToastContext({
          showSuccess: true,
          message: getMessageId(selectedClassification, data.classification),
          values: {
            message: truncateMessage(note.description ?? ''),
            count: `${data.splitEntries.length}`
          }
        });
      } catch (e) {
        setToastContext({
          showError: true,
          message: 'toast.note.split.failure'
        });
        return;
      }

      onComplete();
    },
    [
      note.id,
      note.date,
      note.sources,
      note.description,
      onComplete,
      isTimeSignalFederatedSuggestionsEnabled,
      attributes,
      isEmptyLabelsEnabled,
      ianaName,
      id,
      splitTimeSignal,
      param,
      selectedClassification,
      searchCriteria,
      sortOrder,
      me?.id,
      setToastContext,
      isSmartSuggestionEnabled,
      putSuggestions,
      setNotesToAnimate
    ]
  );

  return {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    saveSplitNote: handleSubmit(splitNote),
    loading: loading || putSuggestionsLoading
  };
};
